import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CategoryService, Category, Subcategory, ProductCategory } from "@/lib/category-service";
import ProductCategoryList from "@/polymet/components/product-category-list";
import BreadcrumbNavigation, {
  BreadcrumbItem,
} from "@/polymet/components/breadcrumb-navigation";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function SubcategoryPage() {
  const { categoryId: categorySlug = "", subCategoryId: subcategorySlug = "" } = useParams();

  const [category, setCategory] = useState<Category | null>(null);
  const [subcategory, setSubcategory] = useState<Subcategory | null>(null);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Initialize category service
  const categoryService = new CategoryService();

  useEffect(() => {
    const fetchData = async () => {
      if (!categorySlug || !subcategorySlug) return;
      
      try {
        setLoading(true);
        
        // Fetch the category by slug
        const categoryData = await categoryService.getCategoryBySlug(categorySlug);
        if (!categoryData) {
          setError('Category not found');
          return;
        }
        
        setCategory(categoryData);
        
        // Fetch the subcategory by slug
        const subcategoryData = await categoryService.getSubcategoryBySlug(categorySlug, subcategorySlug);
        if (!subcategoryData) {
          setError('Subcategory not found');
          return;
        }
        
        setSubcategory(subcategoryData);
        
        // Fetch product categories for this subcategory
        const productCategoriesData = await categoryService.getProductCategoriesBySubcategoryId(subcategoryData.id);
        setProductCategories(productCategoriesData);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [categorySlug, subcategorySlug]);

  // Create breadcrumb items
  const breadcrumbItems: BreadcrumbItem[] = [
    { name: "Home", href: "/" }
  ];
  
  if (category) {
    breadcrumbItems.push({
      name: category.name,
      href: `/${category.slug}`,
    });
  }
  if (subcategory) {
    breadcrumbItems.push({
      name: subcategory.name,
      href: `/${category?.slug}/${subcategory.slug}`,
    });
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center items-center">
        <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        <span className="ml-2 text-lg">Loading subcategory...</span>
      </div>
    );
  }
  
  if (error || !category || !subcategory) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-6">Subcategory Not Found</h1>
          <p className="text-lg text-red-500 mb-4">{error || 'The subcategory you are looking for does not exist.'}</p>
          <Button onClick={() => window.location.href = '/'}>Return to Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <BreadcrumbNavigation items={breadcrumbItems} className="mb-6" />

      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">{subcategory.name}</h1>
        <p className="text-muted-foreground">{subcategory.description}</p>
      </div>

      <ProductCategoryList
        categoryId={category.id}
        categorySlug={category.slug}
        categoryName={category.name}
        subCategoryId={subcategory.id}
        subCategorySlug={subcategory.slug}
        subCategoryName={subcategory.name}
        productCategories={productCategories}
        description={`Browse our selection of ${subcategory.name.toLowerCase()} product categories`}
      />
    </div>
  );
}
